import React, { useRef, useState } from "react";
import { Upload, X, Loader2, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { api, API, formatApiErrorDetail } from "../lib/api";

const MAX_MB = 5;
const MAX_BYTES = MAX_MB * 1024 * 1024;

// Convert stored URL (may be `/api/uploads/xxx`) into an absolute URL for <img>.
export function resolveImageUrl(url) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  const backend = API.replace(/\/api$/, "");
  return `${backend}${url}`;
}

/**
 * Reusable image uploader.
 *  - single mode: value = string URL, onChange(newUrl)
 *  - multi mode: value = string[] of URLs, onChange(nextArray), max prop enforced (default 5)
 */
export default function ImageUploader({
  value, onChange, multi = false, max = 5,
  label = "Enviar imagem", accept = "image/*",
  testId = "image-uploader",
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const list = multi ? (Array.isArray(value) ? value.filter(Boolean) : []) : (value ? [value] : []);

  const uploadOne = async (file) => {
    if (!file) return null;
    if (!file.type?.startsWith("image/")) {
      toast.error("Envie um arquivo de imagem (JPG, PNG, WEBP ou GIF).");
      return null;
    }
    if (file.size > MAX_BYTES) {
      toast.error(`Imagem muito grande (${(file.size / 1024 / 1024).toFixed(1)}MB). Máximo ${MAX_MB}MB.`);
      return null;
    }
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/uploads/image", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data.image_url;
  };

  const onFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      if (multi) {
        const remaining = Math.max(0, max - list.length);
        if (remaining <= 0) {
          toast.error(`Limite de ${max} imagens atingido.`);
          return;
        }
        const arr = Array.from(files).slice(0, remaining);
        const uploaded = [];
        for (const f of arr) {
          const u = await uploadOne(f);
          if (u) uploaded.push(u);
        }
        if (uploaded.length) {
          onChange([...(value || []), ...uploaded]);
          toast.success(`${uploaded.length} imagem(ns) enviada(s).`);
        }
      } else {
        const u = await uploadOne(files[0]);
        if (u) {
          onChange(u);
          toast.success("Imagem enviada.");
        }
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Falha no upload");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = (idx) => {
    if (multi) {
      const next = list.filter((_, i) => i !== idx);
      onChange(next);
    } else {
      onChange("");
    }
  };

  const canAdd = multi ? list.length < max : list.length < 1;
  const remaining = multi ? Math.max(0, max - list.length) : (list.length ? 0 : 1);

  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
        {list.map((url, i) => (
          <div key={`${url}-${i}`} className="relative aspect-square rounded-xl overflow-hidden border border-border bg-muted group">
            <img src={resolveImageUrl(url)} alt="" className="w-full h-full object-cover" />
            <button type="button" onClick={() => remove(i)} data-testid={`${testId}-remove-${i}`}
              className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              aria-label="Remover imagem">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        {canAdd && (
          <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
            data-testid={`${testId}-add`}
            className="aspect-square rounded-xl border-2 border-dashed border-border bg-muted/40 flex flex-col items-center justify-center gap-1 text-muted-foreground hover:bg-muted hover:border-primary/50 transition-colors disabled:opacity-60">
            {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
            <span className="text-[10px] font-medium text-center px-1">{uploading ? "Enviando..." : label}</span>
          </button>
        )}
      </div>
      <p className="text-[11px] text-muted-foreground flex items-center gap-1">
        <ImageIcon className="w-3 h-3" />
        {multi
          ? `${list.length}/${max} imagens · JPG, PNG, WEBP até ${MAX_MB}MB${remaining ? "" : " · limite atingido"}`
          : `JPG, PNG, WEBP até ${MAX_MB}MB`}
      </p>
      <input ref={inputRef} type="file" accept={accept} multiple={multi} className="hidden"
        data-testid={`${testId}-input`}
        onChange={(e) => onFiles(e.target.files)} />
    </div>
  );
}
